import type React from 'react'

export const notifBtn: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 'var(--radius-chip)',
  background: 'linear-gradient(135deg, var(--parkify-accent-light) 0%, var(--parkify-accent) 50%, var(--parkify-accent-strong) 100%)',
  color: '#fff', border: 'none',
  cursor: 'pointer', fontFamily: 'inherit',
}
export const notifGhostBtn: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius-chip)',
  background: 'transparent', color: 'var(--color-gray-700)',
  border: '1px solid var(--color-gray-300)', cursor: 'pointer', fontFamily: 'inherit',
}

// Dim backdrop — covers main content when the chat window is open. Clicking it closes.
export const paneBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1099,
  background: 'rgba(35,31,32,0.42)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  animation: 'avery-backdrop-in 220ms ease-out',
}

// Centred chat window — opened from the omni bar / Cmd+K on any screen other
// than `/avery`. A conversation popped out over the console, not a 420px side
// strip: wide enough for tables and proposal cards to read like a chat app.
// `inset: 0` + `margin: auto` centres a fixed box with explicit size; the 12px
// minimum gutter keeps it a window on a phone instead of a full takeover.
export const panelFrame: React.CSSProperties = {
  position: 'fixed', inset: 0, margin: 'auto', zIndex: 1100,
  width: 'min(880px, calc(100vw - 24px))',
  height: 'min(820px, calc(100dvh - 24px))',
  display: 'flex', flexDirection: 'column',
  background: 'var(--color-white)',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 24px 80px rgba(35,31,32,0.28), 0 4px 16px rgba(74,31,96,0.12)',
  border: '1px solid var(--color-brand-border)',
  fontFamily: "'Montserrat', 'Avenir', sans-serif",
  animation: 'avery-pane-in 220ms cubic-bezier(0.22, 1, 0.36, 1)',
}

// Side padding that centres a 760px reading column inside the chat window.
// Percent padding resolves against the frame's width, so the column stays
// centred at any window size with a 20px floor on narrow ones.
const modalColumnPad = 'max(20px, calc((100% - 760px) / 2))'

// In-flow variant of panelFrame for the full-page `/avery` surface, where the
// chat IS the screen rather than an overlay on one. No fixed positioning (so
// it sits inside <main>'s flow and the sidebar stays usable), no slide-in
// animation (nothing slid in — it's just the page), and — unlike the drawer —
// no card chrome (no border, radius or shadow): a floating rounded card next
// to a rail reads as a widget dropped onto a dashboard, not a screen. This
// fills its column edge-to-edge. Active chat reserves room for console chrome
// and pins the composer while the transcript scrolls. The launchpad overrides
// this height so its cards participate in normal page scrolling.
export const panelFrameInline: React.CSSProperties = {
  position: 'relative', zIndex: 0,
  width: '100%', height: 'calc(100dvh - 160px)', minHeight: 420,
  display: 'flex', flexDirection: 'column',
  background: 'var(--color-white)',
  fontFamily: "'Montserrat', 'Avenir', sans-serif",
}

// No separate inner wrapper needed — panelFrame is the card now.
export const panelInner: React.CSSProperties = {
  width: '100%', height: '100%',
  display: 'flex', flexDirection: 'column',
  overflow: 'hidden',
}

// Plain in-page header for the `/avery` screen — a thin bottom-bordered bar
// like the rest of the console's screens use, not the drawer's branded
// gradient band (that band is what makes the widget read as a support-chat
// popup; a dedicated screen doesn't need to announce itself the same way).
export const panelHeaderInline: React.CSSProperties = {
  background: 'var(--color-white)',
  color: 'var(--color-black)',
  padding: '16px 4px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexShrink: 0,
  borderBottom: '1px solid var(--color-brand-border)',
}

// Light title bar for the chat window — the standard chat-app header rather
// than the old drawer's branded gradient band.
export const panelHeaderModal: React.CSSProperties = {
  background: 'var(--color-white)',
  color: 'var(--color-black)',
  padding: '14px 18px 14px 22px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexShrink: 0,
  borderBottom: '1px solid var(--color-brand-border)',
}

// Dark-on-light counterpart to `wordmark` for the in-page header.
export const wordmarkInline: React.CSSProperties = {
  fontSize: 20, fontWeight: 700, lineHeight: 1,
  letterSpacing: '0.3px', textTransform: 'lowercase', color: 'var(--color-black)',
}

// Static (no pulse) dot for the in-page header — a small status cue, not the
// widget's "look, I'm live!" marketing flourish.
export const onlineDotInline: React.CSSProperties = {
  width: 7, height: 7, borderRadius: '50%', background: '#22a35a',
  flexShrink: 0,
}

// One bouncing dot in the typing indicator; delay staggers the three.
export function typingDot(delaySec: number): React.CSSProperties {
  return {
    width: 7, height: 7, borderRadius: '50%', background: 'var(--color-gray-500)',
    display: 'inline-block',
    animation: `avery-typing-bounce 1.2s ease-in-out ${delaySec}s infinite`,
  }
}

// Dark-on-light counterpart to `iconBtn` for the in-page header.
export const iconBtnInline: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 8,
  background: 'var(--color-gray-100)',
  border: 'none', color: 'var(--color-gray-700)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: 'inherit',
}

export const panelBody: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: `24px ${modalColumnPad}`,
  background: 'var(--color-gray-100)',
}

// Wider gutters for the full-page surface — a real chat screen breathes more
// than a 400px drawer does.
export const panelBodyInline: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '24px clamp(16px, 4vw, 48px)',
  background: 'var(--color-gray-100)',
}

// Staff "Viewing as" scope strip — slim bar between header and body.
export const viewAsBar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 14px', flexShrink: 0,
  background: 'var(--color-gray-50)',
  borderBottom: '1px solid var(--color-brand-border)',
}
export const viewAsLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
  textTransform: 'uppercase', color: 'var(--color-gray-500)', flexShrink: 0,
}
export const viewAsSelect: React.CSSProperties = {
  flex: 1, minWidth: 0, padding: '6px 10px', borderRadius: 8,
  border: '1.5px solid var(--color-gray-300)', fontSize: 12,
  fontFamily: 'inherit', background: 'var(--color-white)',
  color: 'var(--color-black)',
}
export const viewAsClear: React.CSSProperties = {
  flexShrink: 0, width: 26, height: 26, borderRadius: 8,
  border: '1px solid var(--color-gray-300)', background: 'var(--color-white)',
  color: 'var(--color-gray-700)', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
}

export const composer: React.CSSProperties = {
  display: 'flex', gap: 8, padding: `16px ${modalColumnPad}`, alignItems: 'center',
  borderTop: '1px solid var(--color-brand-border)',
  background: 'var(--color-white)',
  flexShrink: 0,
}

// Wider gutters for the full-page surface, matching `panelBodyInline`.
export const composerInlineWrap: React.CSSProperties = {
  display: 'flex', gap: 8, padding: '16px clamp(16px, 4vw, 48px)', alignItems: 'center',
  borderTop: '1px solid var(--color-brand-border)',
  background: 'var(--color-white)',
  flexShrink: 0,
}

export const composerInput: React.CSSProperties = {
  flex: 1, padding: '11px 15px', borderRadius: 999,
  border: '1.5px solid var(--color-brand-border)', fontSize: 14,
  fontFamily: 'inherit', background: 'var(--color-gray-100)',
  color: 'var(--color-black)', outline: 'none',
  transition: 'border-color 0.15s, background 0.15s',
}

export const sendBtn: React.CSSProperties = {
  flexShrink: 0, padding: '10px 18px', borderRadius: 999,
  background: 'linear-gradient(135deg, var(--parkify-accent-light) 0%, var(--parkify-accent) 50%, var(--parkify-accent-strong) 100%)',
  color: '#fff', border: 'none', fontFamily: 'inherit',
  fontSize: 13, fontWeight: 700, letterSpacing: '0.3px',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 6px rgba(92,46,112,0.45)',
}

// ── Launchpad ───────────────────────────────────────────────────────────────
// The full-page surface before the operator has said anything. One centred
// column: greeting, the composer itself, the teaching chips, then whatever
// Avery noticed on her own. The docked composer is NOT rendered in this state
// — two inputs on one screen is the thing a chat surface never does.

export const launchpadWrap: React.CSSProperties = {
  maxWidth: 760, margin: '0 auto', padding: '28px 0 8px',
  display: 'flex', flexDirection: 'column',
}

export const launchpadGreeting: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: 11, marginBottom: 22,
}
export const launchpadGreetingMark: React.CSSProperties = {
  fontSize: 24, color: 'var(--parkify-accent)', lineHeight: 1,
}
export const launchpadGreetingText: React.CSSProperties = {
  // Scales down rather than wrapping "Good evening," off "Billy" on a phone.
  fontSize: 'clamp(21px, 5.2vw, 29px)', fontWeight: 800,
  color: 'var(--color-black)', letterSpacing: '-0.8px', textAlign: 'center',
}

// The composer as the subject of the page rather than a strip at its foot.
export const composerLaunchpadWrap: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 9, flexShrink: 0,
  background: 'var(--color-white)',
  border: '1px solid var(--color-brand-border)',
  borderRadius: 16,
  boxShadow: '0 2px 14px rgba(0,0,0,0.05)',
  padding: '15px 16px 12px',
  marginBottom: 16,
}
export const composerLaunchpadInput: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
  background: 'transparent', padding: '4px 2px 8px',
  fontSize: 15.5, fontWeight: 500, fontFamily: 'inherit',
  color: 'var(--color-black)',
}
// Footer row inside the composer card: attach, org pill, then the submit.
export const composerLaunchpadFoot: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 9,
}
export const launchpadOrgPill: React.CSSProperties = {
  fontSize: 11.5, fontWeight: 700, color: 'var(--color-gray-700)',
  background: 'var(--color-gray-100)',
  border: '1px solid var(--color-brand-border)',
  borderRadius: 999, padding: '5px 11px',
  maxWidth: 220, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
}
export const launchpadSendBtn: React.CSSProperties = {
  width: 30, height: 30, borderRadius: '50%', border: 'none',
  color: '#fff', fontSize: 14, fontFamily: 'inherit',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, marginLeft: 'auto',
}

export const launchpadChipRow: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 8,
  justifyContent: 'center', marginBottom: 8,
}
export const launchpadChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 7,
  background: 'var(--color-white)',
  border: '1px solid var(--color-brand-border)',
  borderRadius: 11, padding: '9px 13px', cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
  color: 'var(--color-black)',
}
export const launchpadMoreBtn: React.CSSProperties = {
  ...launchpadChip, color: 'var(--color-gray-700)',
}
export const launchpadMoreItem: React.CSSProperties = {
  textAlign: 'left', background: 'var(--color-gray-100)',
  border: '1px solid var(--color-brand-border)', borderRadius: 9,
  padding: '10px 13px', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 12.5, fontWeight: 600, color: 'var(--color-gray-700)',
}

export const launchpadSectionLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: 'var(--color-gray-500)',
  margin: '14px 0 9px', padding: '0 2px',
}

// ── Screenshot attachments ──────────────────────────────────────────────────
// The 📎 button sits inside the composer row; the tray of staged thumbnails
// sits ABOVE it so a chip never squeezes the text field on a narrow panel.

export const attachBtn: React.CSSProperties = {
  flexShrink: 0, width: 34, height: 34, borderRadius: 999,
  display: 'grid', placeItems: 'center', fontSize: 15, lineHeight: 1,
  background: 'var(--color-white)', color: 'var(--parkify-accent)',
  border: '1.5px solid var(--color-brand-border)', fontFamily: 'inherit',
}

export const attachTray: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 6,
  padding: '8px 12px 0', alignItems: 'center',
}

export const attachChip: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, maxWidth: '100%',
  padding: '4px 6px 4px 4px', borderRadius: 8,
  background: 'var(--color-gray-100)', border: '1px solid var(--color-brand-border)',
}

export const attachThumb: React.CSSProperties = {
  width: 28, height: 28, objectFit: 'cover', borderRadius: 5, display: 'block',
}

export const attachChipName: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: 'var(--color-gray-700)',
  maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

export const attachChipStatus: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: 'var(--color-gray-500)', fontStyle: 'italic',
}

export const attachChipRemove: React.CSSProperties = {
  flexShrink: 0, width: 18, height: 18, borderRadius: 999, border: 'none',
  background: 'transparent', color: 'var(--color-gray-500)',
  fontSize: 14, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit',
}

export const attachError: React.CSSProperties = {
  flexBasis: '100%', fontSize: 11, fontWeight: 600, color: 'var(--color-danger)',
}

// Pill chip — mirrors the website widget's quick-reply chips.
export const suggestionBtn: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, textAlign: 'left',
  padding: '6px 14px', borderRadius: 'var(--radius-chip)', whiteSpace: 'nowrap',
  background: 'var(--color-white)', color: 'var(--parkify-accent)',
  border: '1.5px solid var(--parkify-accent)', cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 1px 2px color-mix(in srgb, var(--parkify-accent) 12%, transparent), inset 0 1px 0 rgba(255,255,255,0.6)',
}
